import * as swcHelpers from '@swc/helpers';
import { getIncludedVatAmount } from '@grandvisionhq/analytics';
import { isDefined, getLocalizedValue, getStringAttrValue, getAttrValue, getJoinedLocalizedAttrValues, getLocalizedAttrValue, getLocalizedString, formatPrice } from '@grandvisionhq/utils';
import { DiscountType } from '../../../types.js';
import { createAltTag } from '../../../utils/create-alt-tag.js';
import { getDiscount } from '../../../utils/get-discount.js';
import { getDiscountDescription } from '../../../utils/get-discount-description.js';
import { getProductTag } from '../../../utils/get-product-tag.js';

const isFullVariantModel = (product)=>'id' in product
;
const isLightProductModel = (product)=>'objectID' in product
;
const mapProductProjectionToListingProduct = (product, intl, priceSegment)=>{
    var ref, ref1, ref2, ref3, ref4, ref5, ref6, ref7, ref8;
    const { getLabel , locale  } = intl;
    const labels = {
        lens: getLabel(`products.unit.contactLens`),
        lenses: getLabel(`products.unit.contactLenses`),
        perBox: getLabel(`products.perBox`)
    };
    const masterVariant = mapVariantInfo(product.masterVariant, locale, labels, priceSegment);
    const variants = product.variants.map((variant)=>mapVariantInfo(variant, locale, labels, priceSegment)
    ).filter(isDefined);
    if (!masterVariant) {
        //We should always have a mastervariant
        console.error(`ERROR: No masterVariant found for product.slug: ${product.slug.en}`);
        return undefined;
    }
    if (!((ref = product.productType.obj) === null || ref === void 0 ? void 0 : ref.name) || ((ref1 = product.productType.obj) === null || ref1 === void 0 ? void 0 : ref1.name) === 'Additional-cart-costs') {
        var ref9;
        console.error(`ERROR: No valid productType received for product.slug: ${product.slug.en}`);
        console.error(`ERROR: productType: ${(ref9 = product.productType.obj) === null || ref9 === void 0 ? void 0 : ref9.name}`);
        return undefined;
    }
    const productType = product.productType.obj.name;
    const includedVatAmount = getIncludedVatAmount(product === null || product === void 0 ? void 0 : (ref2 = product.taxCategory) === null || ref2 === void 0 ? void 0 : (ref3 = ref2.obj) === null || ref3 === void 0 ? void 0 : ref3.rates);
    const categoryName = (ref4 = product.categories) === null || ref4 === void 0 ? void 0 : (ref5 = ref4[0]) === null || ref5 === void 0 ? void 0 : (ref6 = ref5.obj) === null || ref6 === void 0 ? void 0 : ref6.name;
    const category = categoryName ? getLocalizedValue(categoryName, 'en') : undefined;
    const isOnStock = isLightProductModel(product) ? product.isOnStock : undefined;
    const productOffer = isLightProductModel(product) ? product === null || product === void 0 ? void 0 : product.offering : undefined;
    const offering = productOffer ? {
        includesLenses: productOffer === null || productOffer === void 0 ? void 0 : productOffer.includesLenses
    } : undefined;
    const ratingsSummary = !isLightProductModel(product) ? {
        average: (ref7 = product.ratingsSummary) === null || ref7 === void 0 ? void 0 : ref7.average,
        reviewCount: (ref8 = product.ratingsSummary) === null || ref8 === void 0 ? void 0 : ref8.reviewCount
    } : undefined;
    return {
        category,
        includedVatAmount,
        isOnStock,
        masterVariant,
        name: {
            en: product.name.en,
            localized: getLocalizedValue(product.name, locale)
        },
        objectID: isLightProductModel(product) ? product.objectID : '',
        offering,
        productType,
        ratingsSummary,
        slug: getLocalizedValue(product.slug, locale),
        variants,
        isOfferedWithPrescriptions: Boolean(product.masterVariant.isOfferedWithPrescriptions)
    };
};
const mapVariantInfo = (variant, locale, labels, priceSegment)=>{
    var ref;
    const priceMap = !isFullVariantModel(variant) ? variant.priceMap : undefined;
    const price = mapPrice(variant.prices, locale, priceMap, priceSegment);
    const altTag = createAltTag({
        variant,
        locale,
        labels
    });
    if (!price) {
        //We should always have a valid price
        console.error(`ERROR: no valid price for variant with sku: ${variant.sku}`);
        return undefined;
    }
    const packageOption = getStringAttrValue(variant, 'packageOption');
    const solutionType = getStringAttrValue(variant, 'solutionType');
    const power = getStringAttrValue(variant, 'power');
    const packageQuantity = getAttrValue(variant, 'packageQuantity') ? Number(getAttrValue(variant, 'packageQuantity')) : undefined;
    const priceIncluded = getAttrValue(variant, 'priceIncluded');
    const priceIncludedAmount = priceIncluded && typeof priceIncluded === 'object' ? Number(priceIncluded.centAmount) : undefined;
    const isExclusiveBrand = isDefined(getAttrValue(variant, 'exclusiveBrand')) ? Boolean(getAttrValue(variant, 'exclusiveBrand')) : undefined;
    const frameColor = {
        en: getJoinedLocalizedAttrValues(variant, 'frameColor', 'en'),
        localized: getJoinedLocalizedAttrValues(variant, 'frameColor', locale)
    };
    const lensColor = getJoinedLocalizedAttrValues(variant, 'lensColor', locale);
    const frameSize = getLocalizedAttrValue(variant, 'frameSize', locale);
    const consolidationFacet = frameSize ? [
        frameColor.localized,
        lensColor
    ].filter(Boolean).join('-') : null;
    return {
        brand: {
            name: getStringAttrValue(variant, 'brand') || '',
            model: getStringAttrValue(variant, 'brandModel') || '',
            modelCode: getStringAttrValue(variant, 'brandModelCode') || ''
        },
        erpId: getStringAttrValue(variant, 'erpId') || '',
        description: getLocalizedAttrValue(variant, 'description', locale),
        frame: {
            color: frameColor,
            material: {
                en: getLocalizedAttrValue(variant, 'frameMaterial', 'en')
            },
            shape: {
                en: getLocalizedAttrValue(variant, 'frameShape', 'en')
            },
            type: {
                en: getLocalizedAttrValue(variant, 'frameType', 'en')
            }
        },
        faceShape: {
            en: getJoinedLocalizedAttrValues(variant, 'faceShape', 'en')
        },
        gender: {
            en: getJoinedLocalizedAttrValues(variant, 'gender', 'en')
        },
        lensColor: getJoinedLocalizedAttrValues(variant, 'lensColor', locale),
        lensType: {
            en: getLocalizedAttrValue(variant, 'lensType', 'en')
        },
        replacementFrequency: getLocalizedAttrValue(variant, 'replacementFrequency', locale),
        material: getLocalizedAttrValue(variant, 'material', locale),
        accessoryType: {
            en: getLocalizedAttrValue(variant, 'accessoryType', 'en'),
            localized: getLocalizedAttrValue(variant, 'accessoryType', locale)
        },
        accessoryColor: {
            en: getJoinedLocalizedAttrValues(variant, 'accessoryColor', 'en'),
            localized: getJoinedLocalizedAttrValues(variant, 'accessoryColor', locale)
        },
        contactLensCareType: getLocalizedAttrValue(variant, 'contactLensCareType', locale),
        isInsuranceReimbursable: Boolean(getAttrValue(variant, 'isInsuranceReimbursable')),
        power,
        priceIncludedAmount,
        packageOption,
        packageQuantity,
        solutionType,
        isExclusiveBrand,
        name: variant.name || '',
        sku: String(variant.sku),
        tag: getProductTag(variant, locale),
        images: (ref = variant.media) === null || ref === void 0 ? void 0 : ref.images.map((img)=>swcHelpers.objectSpread({}, img, {
                altTag
            })
        ),
        pricing: {
            bestPrice: variant.bestPrice,
            price,
            pricePerUnit: variant.pricePerUnit || undefined
        },
        consolidationFacet
    };
};
const getPriceMapItemDiscount = (discount, currencyCode, locale)=>{
    const discountValue = discount.value;
    if (typeof discountValue === 'undefined' || discountValue === 0) {
        return undefined;
    }
    if (discount.type === DiscountType.Absolute) {
        return `-${formatPrice({
            locale,
            value: discountValue,
            currency: currencyCode,
            isCentAmount: false
        })}`;
    }
    if (discount.type === DiscountType.Relative) {
        return `-${discountValue}%`;
    }
    return undefined;
};
const mapPriceFromPriceMap = (priceItem, locale)=>{
    var ref, ref10, ref11, ref12, ref13, ref14, ref15, ref16, ref17, ref18, ref19, ref20, ref21;
    const originalPrice = (ref = priceItem.originalPrice) === null || ref === void 0 ? void 0 : ref.amount;
    const currencyCode = (ref10 = priceItem.originalPrice) === null || ref10 === void 0 ? void 0 : ref10.currencyCode;
    const discountPrice = (ref11 = priceItem.discountedPrice) === null || ref11 === void 0 ? void 0 : (ref12 = ref11.price) === null || ref12 === void 0 ? void 0 : ref12.amount;
    const discountCurrencyCode = (ref13 = priceItem.discountedPrice) === null || ref13 === void 0 ? void 0 : (ref14 = ref13.price) === null || ref14 === void 0 ? void 0 : ref14.currencyCode;
    const discountInformation = (ref15 = priceItem.discountedPrice) === null || ref15 === void 0 ? void 0 : ref15.discountInformation;
    if (!originalPrice || !currencyCode) {
        return undefined;
    }
    return {
        originalPrice: originalPrice * 100,
        isCentAmount: true,
        currencyCode,
        discount: discountPrice && discountCurrencyCode ? {
            price: discountPrice * 100,
            badge: discountInformation ? getPriceMapItemDiscount(discountInformation, discountCurrencyCode, locale) : undefined,
            description: discountInformation ? getLocalizedString(discountInformation.description, locale) : undefined,
            validFrom: (ref16 = priceItem.discountedPrice) === null || ref16 === void 0 ? void 0 : (ref17 = ref16.discountInformation) === null || ref17 === void 0 ? void 0 : (ref18 = ref17.validity) === null || ref18 === void 0 ? void 0 : ref18.validFrom,
            validUntil: (ref19 = priceItem.discountedPrice) === null || ref19 === void 0 ? void 0 : (ref20 = ref19.discountInformation) === null || ref20 === void 0 ? void 0 : (ref21 = ref20.validity) === null || ref21 === void 0 ? void 0 : ref21.validUntil
        } : undefined
    };
};
const mapPrice = (prices = [], locale, priceMap, priceSegment)=>{
    var ref, ref22, ref23, ref24, ref25, ref26, ref27;
    // use price from priceMap if our segment exists otherwise fallback to regular pricing strategy
    if (priceMap && priceSegment && priceMap[priceSegment]) {
        return mapPriceFromPriceMap(priceMap[priceSegment], locale);
    }
    const price = prices[0];
    const discount = price === null || price === void 0 ? void 0 : (ref = price.discounted) === null || ref === void 0 ? void 0 : ref.value.centAmount;
    if (!price) {
        return undefined;
    }
    return {
        originalPrice: price.value.centAmount,
        currencyCode: price.value.currencyCode,
        isCentAmount: true,
        discount: discount ? {
            description: getDiscountDescription(prices, locale),
            price: discount,
            badge: getDiscount(prices, locale),
            validFrom: price === null || price === void 0 ? void 0 : (ref22 = price.discounted) === null || ref22 === void 0 ? void 0 : (ref23 = ref22.discount) === null || ref23 === void 0 ? void 0 : (ref24 = ref23.obj) === null || ref24 === void 0 ? void 0 : ref24.validFrom,
            validUntil: price === null || price === void 0 ? void 0 : (ref25 = price.discounted) === null || ref25 === void 0 ? void 0 : (ref26 = ref25.discount) === null || ref26 === void 0 ? void 0 : (ref27 = ref26.obj) === null || ref27 === void 0 ? void 0 : ref27.validUntil
        } : undefined
    };
};

export { mapProductProjectionToListingProduct };
//# sourceMappingURL=map-product-projection-to-listing-product.js.map
